import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import * as Sentry from '@sentry/react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import './index.css'
import './i18n' // inicializa i18next antes do render (Release C)
import App from './App.tsx'

// React Query — usado pelo LangSwitcher pra `invalidateQueries()` ao trocar
// idioma (limpa caches que dependem de Accept-Language: posts do blog,
// landings traduzidas, /institutional, etc.). Sem o Provider, qualquer
// `useQueryClient()`/`useQuery()` lança "No QueryClient set" — vide
// LangSwitcher.tsx:32, renderizado em PublicHeader/AppShell.
//
// Defaults conservadores — única instância pra toda a app.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,    // 5min — cache "fresco" sem refetch
      gcTime: 30 * 60 * 1000,       // 30min — descarta cache não usado
      retry: 1,
      refetchOnWindowFocus: false,  // evita refetch agressivo ao trocar de aba
    },
  },
})

// `__APP_VERSION__` é injetado pelo `vite.config.ts` (define) a partir do
// `package.json` — release fica sempre alinhada com o bump, sem precisar
// editar 2 lugares (cf. CLAUDE.md item 22).
declare const __APP_VERSION__: string

// Auto-recuperação de chunk velho pós-deploy ("Importing a module script
// failed"): quando sai um deploy novo, os hashes dos chunks lazy mudam;
// abas antigas (Safari iOS é o pior caso) tentam importar um chunk que não
// existe mais → 404 → erro. O Vite dispara `vite:preloadError`. Recarregar
// 1x resolve transparente (pega os assets novos). Flag em sessionStorage
// evita loop se o chunk realmente não existir mesmo após o reload — nesse
// caso deixa o erro propagar pro ErrorBoundary normalmente.
const PRELOAD_RELOAD_KEY = 'vite-preload-reloaded'
window.addEventListener('vite:preloadError', (event) => {
  try {
    if (sessionStorage.getItem(PRELOAD_RELOAD_KEY)) return // já tentou — não loopar
    sessionStorage.setItem(PRELOAD_RELOAD_KEY, '1')
    event.preventDefault() // evita o ErrorBoundary nesta 1ª falha — vamos recarregar
    window.location.reload()
  } catch {
    // sessionStorage indisponível (ver issue localStorage): deixa propagar
  }
})
// Carregou OK → limpa a flag pra que uma falha futura (próximo deploy) possa
// recarregar de novo.
window.addEventListener('load', () => {
  try { sessionStorage.removeItem(PRELOAD_RELOAD_KEY) } catch { /* noop */ }
})

// Inicializa Sentry apenas se o DSN estiver configurado (não roda em dev sem .env)
if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE, // "development" | "production"
    release: `bolao-elite@${__APP_VERSION__}`,
    // Captura apenas erros — sem rastreamento de performance (tracesSampleRate omitido)
    // sendDefaultPii: false — não coleta IP automaticamente (LGPD)
    sendDefaultPii: false,
    // Filtros pra não inundar Sentry com ruído conhecido. Cada item aqui
    // tem origem auditada num issue Sentry real (BOLAO-ELITE-*).
    ignoreErrors: [
      // [BOLAO-ELITE-Q] AdSense (adsbygoogle.js) falhando ao mandar beacon
      // pro googlesyndication.com quando o browser tem adblocker (Samsung
      // Internet nativo, uBlock, DNS filtrado). Não é bug nosso.
      /Failed to fetch.*pagead2\.googlesyndication\.com/,
      /adsbygoogle/,
      /pagead\/gen_204/,
      // [BOLAO-ELITE-R] Promise rejection vazia + AxiosError network quando
      // o user está em 4G/5G instável e troca de rede no meio de uma request.
      // Volume alto em iOS Safari mobile; o try/catch local já trata, esse
      // filtro só evita o duplo-report do axios interceptor.
      'Network Error',
      'Load failed',
      /^Network request failed$/,
      'Non-Error promise rejection captured with value: undefined',
      // [BOLAO-ELITE-V] NotFoundError em insertBefore — browser extensions
      // (Grammarly, password managers) ou Google Translate modificam o DOM
      // e o React reconcile perde sync. Sintoma conhecido, sem fix possível
      // no nosso código.
      'NotFoundError',
      /The object can not be found here/,
    ],
    denyUrls: [
      // Erros oriundos de scripts terceiros nunca são acionáveis do nosso
      // lado. Sentry descarta o evento se o frame top vier desses domínios.
      /pagead2\.googlesyndication\.com/,
      /googletagmanager\.com/,
      /google-analytics\.com/,
      /doubleclick\.net/,
      /googleadservices\.com/,
    ],
    beforeSend(event) {
      // Em dev, loga no console mas não envia ao Sentry
      if (import.meta.env.DEV) {
        console.error('[Sentry (dev - não enviado)]', event)
        return null
      }
      return event
    },
  })
}

// Registrar Service Worker para Web Push Notifications
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.register('/sw.js')
    .then((registration) => {
      console.log('Service Worker registered:', registration);
    })
    .catch((error) => {
      console.error('Service Worker registration failed:', error);
    });
}

const root = document.getElementById('root')!

createRoot(root).render(
  <StrictMode>
    <Sentry.ErrorBoundary
      fallback={
        <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
          <div className="text-center max-w-sm">
            <p className="text-4xl mb-4">😵</p>
            <h1 className="text-lg font-bold text-gray-900 mb-2">Algo deu errado</h1>
            <p className="text-sm text-gray-500 mb-6">
              O erro foi registrado automaticamente. Tente recarregar a página.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors"
            >
              Recarregar página
            </button>
          </div>
        </div>
      }
    >
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  </StrictMode>,
)
